import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import PageHeader from '../shared/PageHeader';
import { DataTable } from '../../components/Tables';
import { rolesApi, permissionsApi, extractList } from '../../api/facades';
import { useAuth } from '../../context/AuthContext';

const RolesListPage: React.FC = () => {
  const { user: authUser } = useAuth();
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [editingRole, setEditingRole] = React.useState<any | null>(null);
  const [formSlug, setFormSlug] = React.useState('');
  const [formDescription, setFormDescription] = React.useState('');
  const [formIsActive, setFormIsActive] = React.useState<'Yes' | 'No'>('Yes');
  const [formPermissionIds, setFormPermissionIds] = React.useState<number[]>([]);

  const { data: rolesRes, isLoading: loadingRoles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list(),
  });
  const roles = extractList(rolesRes);
  const isSuperAdminUser = useMemo(
    () =>
      Array.isArray((authUser as any)?.roles) &&
      (authUser as any).roles.some((r: any) => r?.slug === 'super_admin'),
    [authUser],
  );
  const visibleRoles = useMemo(
    () =>
      isSuperAdminUser ? roles : roles.filter((r: any) => r?.slug !== 'super_admin'),
    [roles, isSuperAdminUser],
  );

  const { data: permsRes } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => permissionsApi.list(),
  });
  const perms = extractList(permsRes);

  const createMu = useMutation({
    mutationFn: () =>
      rolesApi.create({
        slug: formSlug,
        description: formDescription || undefined,
        is_active: formIsActive,
        permission_ids: formPermissionIds,
      }),
    onSuccess: () => {
      toast.success('Role created.');
      setIsCreateOpen(false);
      setFormSlug('');
      setFormDescription('');
      setFormIsActive('Yes');
      setFormPermissionIds([]);
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (e: any) => {
      toast.error(e?.body?.message || e?.message || 'Create failed.');
    },
  });

  const updateMu = useMutation({
    mutationFn: () =>
      rolesApi.update(editingRole.id, {
        slug: formSlug,
        description: formDescription || undefined,
        is_active: formIsActive,
        permission_ids: formPermissionIds,
      }),
    onSuccess: () => {
      toast.success('Role updated.');
      setIsEditOpen(false);
      setEditingRole(null);
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (e: any) => {
      toast.error(e?.body?.message || e?.message || 'Update failed.');
    },
  });

  const openCreate = () => {
    setEditingRole(null);
    setFormSlug('');
    setFormDescription('');
    setFormIsActive('Yes');
    setFormPermissionIds([]);
    setIsCreateOpen(true);
  };

  const openEdit = (role: any) => {
    setEditingRole(role);
    setFormSlug(role.slug ?? '');
    setFormDescription(role.description ?? '');
    setFormIsActive(role.is_active === 'Yes' ? 'Yes' : 'No');
    const permIds =
      Array.isArray(role.permissions) && role.permissions.length
        ? role.permissions.map((p: any) => p.id).filter((id: any) => id != null)
        : [];
    setFormPermissionIds(permIds);
    setIsEditOpen(true);
  };

  const columns = [
    { key: 'slug', label: 'Slug' },
    { key: 'description', label: 'Description' },
    {
      key: 'is_active',
      label: 'Active',
      render: (r: any) => (r.is_active === 'Yes' ? 'Yes' : 'No'),
    },
    {
      key: 'permissions',
      label: 'Permissions',
      render: (r: any) =>
        Array.isArray(r.permissions) && r.permissions.length
          ? r.permissions.map((p: any) => p.slug).join(', ')
          : '—',
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r: any) => (
        <button
          type="button"
          className="text-xs text-primary-600 hover:text-primary-700"
          onClick={() => openEdit(r)}
        >
          Edit
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Roles"
        subtitle="Manage roles and their permissions. Editing can be expanded later as needed."
        primaryAction={{ label: 'Add role', onClick: openCreate }}
      />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <DataTable
          columns={columns}
          data={visibleRoles}
          loading={loadingRoles}
          emptyMessage="No roles."
        />
        {perms.length > 0 && (
          <div className="mt-6 text-xs text-slate-500 dark:text-slate-400">
            <div className="font-semibold mb-1">Available permissions:</div>
            <div>{perms.map((p: any) => p.slug).join(', ')}</div>
          </div>
        )}
      </div>

      {(isCreateOpen || isEditOpen) && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {isCreateOpen ? 'Add role' : 'Edit role'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setIsCreateOpen(false);
                  setIsEditOpen(false);
                  setEditingRole(null);
                }}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <form
              className="px-6 py-5 space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (!formSlug) {
                  toast.error('Slug is required.');
                  return;
                }
                if (
                  !isSuperAdminUser &&
                  formSlug.trim().toLowerCase() === 'super_admin'
                ) {
                  toast.error('The super_admin role is reserved for platform use.');
                  return;
                }
                if (isCreateOpen) {
                  createMu.mutate();
                } else if (isEditOpen && editingRole) {
                  updateMu.mutate();
                }
              }}
            >
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Slug
                </label>
                <input
                  value={formSlug}
                  onChange={(e) => setFormSlug(e.target.value)}
                  className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                  placeholder="e.g. admin, attorney"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Description
                </label>
                <input
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                  placeholder="Role description"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Permissions
                </label>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-auto">
                  {perms.map((p: any) => (
                    <label
                      key={p.id}
                      className="inline-flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300"
                    >
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        checked={formPermissionIds.includes(p.id)}
                        onChange={(e) =>
                          setFormPermissionIds((prev) =>
                            e.target.checked
                              ? [...prev, p.id]
                              : prev.filter((id) => id !== p.id),
                          )
                        }
                      />
                      <span>{p.slug}</span>
                    </label>
                  ))}
                  {perms.length === 0 && (
                    <p className="text-xs text-slate-500">
                      No permissions defined yet.
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Status
                </label>
                <select
                  value={formIsActive}
                  onChange={(e) =>
                    setFormIsActive(e.target.value === 'Yes' ? 'Yes' : 'No')
                  }
                  className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                >
                  <option value="Yes">Active</option>
                  <option value="No">Inactive</option>
                </select>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateOpen(false);
                    setIsEditOpen(false);
                    setEditingRole(null);
                  }}
                  className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMu.isPending || updateMu.isPending}
                  className="px-3 py-1.5 text-xs rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                >
                  {isCreateOpen
                    ? createMu.isPending
                      ? 'Creating...'
                      : 'Create role'
                    : updateMu.isPending
                    ? 'Saving...'
                    : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesListPage;

